// src/i18n/useLanguagePreference.ts
//
// Persists the user's language choice and exposes whether they've
// EXPLICITLY made one. The LanguageGate uses `hasExplicitChoice`
// to decide whether to render — auto-detection from
// navigator.language never counts as explicit; only an active
// selection (this hook's setter, or a value on the user's profile)
// dismisses the gate.
//
// Storage strategy:
//   - localStorage key `instant-tours-language` holds the selected
//     `englishName` ("English", "Spanish", ...). Same wire format
//     used everywhere else in the app (server resolveUserLanguage,
//     travelProfile.demographics.primaryLanguage, etc.) so we don't
//     fork the keying.
//   - Authenticated users: profile.travelProfile.demographics.primaryLanguage
//     is also treated as an explicit choice. If both exist, profile
//     wins on read (canonical source for logged-in users).
//   - Anonymous users: localStorage IS the source of truth. When
//     they later sign up, the consuming code can copy localStorage
//     into the new profile (not done in this hook — separation of
//     concerns).
//
// Cookies vs localStorage: we use localStorage. The same pattern as
// the existing cookie-consent flow ('instant-tours-cookie-consent').
// localStorage persists indefinitely on the same origin, doesn't
// expire, and survives PWA install — cookies would expire on a
// rolling window unless we set a long Max-Age and need to handle
// SameSite/Secure flags. If we ever need cross-subdomain support,
// switch this to a cookie; the rest of the app reads through this
// hook so the migration is local.
//
// ── Refresh semantics ─────────────────────────────────────────
// An earlier version of this hook scheduled a hard
// `window.location.reload()` after persisting the new language.
// That was a bandaid for the case where translation strings were
// cached in non-reactive call sites — but in practice every
// caller goes through `useT()`, which subscribes to dictionary
// installs via the `subscribers` set in `./useT.ts`. Reloading
// caused a visible double-refresh:
//   1. setStored(value) → React re-renders → useTranslationStatus
//      effect re-fires with the new language code → the standby
//      screen flashes briefly while loadTranslations() runs.
//   2. window.location.reload() → full page reload → standby
//      screen again, then app boots.
//
// Removing the reload makes language switches feel instantaneous
// when the dictionary is already cached, and shows a single
// standby screen when generation is needed. If you find a code
// path that still reads stale translations after a switch,
// migrate it to `useT()` rather than reintroducing the reload.

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { findByEnglishName, LanguageEntry } from './languages';

const STORAGE_KEY = 'instant-tours-language';

function readStorage(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    // localStorage can throw in private mode / quota / disabled.
    // Silent fall-through — gate will just keep showing until the
    // user has profile-side language set. Acceptable degradation.
    return null;
  }
}

function writeStorage(value: string | null): void {
  try {
    if (typeof window === 'undefined') return;
    if (value == null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // ignore
  }
}

export interface LanguagePreferenceState {
  /** The chosen language entry, or null if no explicit choice exists. */
  language: LanguageEntry | null;
  /** Display name as stored ("English", "Spanish", ...) or null. */
  englishName: string | null;
  /** True ONLY if the user has actively selected a language —
   *  via this hook OR via their profile. Browser auto-detection
   *  does not count; the gate uses this flag to decide whether
   *  to render itself. */
  hasExplicitChoice: boolean;
  /** Persist a new choice (and propagate to logged-in profile if
   *  applicable). Pass `null` to clear. */
  setLanguage: (entry: LanguageEntry | null) => void;
}

export function useLanguagePreference(): LanguagePreferenceState {
  const profile = useAuthStore((s) => s.profile);
  const authMode = useAuthStore((s) => s.authMode);
  const updateUserProfile = useAuthStore((s) => s.updateUserProfile);
  // Initial value pulled synchronously from localStorage so we don't
  // flash the gate on hydrate when there's actually a saved choice.
  const [stored, setStored] = useState<string | null>(() => readStorage());

  // Profile language wins when authenticated.
  const profileLang =
    profile?.travelProfile?.demographics?.primaryLanguage || null;

  const englishName = profileLang || stored;
  const language = findByEnglishName(englishName);
  const hasExplicitChoice = !!englishName;

  // Listen for cross-tab updates so picking a language in one tab
  // dismisses the gate in another.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setStored(readStorage());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setLanguage = useCallback(
    (entry: LanguageEntry | null) => {
      const value = entry?.englishName ?? null;
      writeStorage(value);
      setStored(value);
      // No `window.location.reload()` here — the reactive flow
      // handles propagation:
      //   - `englishName` changes → `language` changes →
      //   - `useTranslationStatus(language)` effect re-fires →
      //     calls loadTranslations(newLang) → installDictionary →
      //   - every `useT()` consumer re-renders with the new
      //     dictionary entries.
      // See module-level comment for the history.

      // If logged in (registered, not anonymous), also write to the
      // profile. Keeps the choice durable across devices and fits
      // the value backend AI calls already prefer. The store's
      // `updateUserProfile` only writes the keys we pass with
      // `merge:true`, so we don't drift other profile fields. We
      // defensive-spread the existing travelProfile / demographics
      // so missing sub-objects don't wipe sibling fields.
      if (authMode === 'registered' && updateUserProfile) {
        const existingTravel = profile?.travelProfile;
        const existingDemo = existingTravel?.demographics;
        // Best-effort — don't await; UI dismisses immediately.
        updateUserProfile({
          travelProfile: {
            ...(existingTravel as object),
            demographics: {
              ...(existingDemo as object),
              primaryLanguage: value,
            },
          } as any,
        }).catch((err) => {
          console.warn('useLanguagePreference: profile write failed:', err);
        });
      }
    },
    [authMode, profile, updateUserProfile]
  );

  return {
    language,
    englishName,
    hasExplicitChoice,
    setLanguage,
  };
}
